import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  CircularProgress,
  Box,
  Typography,
  AutocompleteRenderInputParams
} from '@mui/material';
import { StyledAutocomplete, TextFieldStyled } from '../PaginatedSearchSelect/styles'; // Re-use styles or creating new ones?
// We need to override the style for MultiSelect to not have minWidth
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { OptionType, PaginatedSearchSelectProps } from '../../interfaces/paginatedSearchSelect.interface';

// Custom Dropdown Icon
const CustomDropdownIcon: React.FC<SvgIconProps> = (props) => (
  <ArrowDropDownIcon sx={{ color: '#5b8c6f' }} {...props} />
);

// Custom Popper to bypass Popper.js calculations entirely for Zoom compatibility
const CustomPopper = (props: any) => {
  const { disablePortal, anchorEl, open, placement, modifiers, style, ...other } = props;
  if (!open) return null;
  return (
    <Box
      {...other}
      sx={{
        position: 'absolute !important',
        top: '100% !important',
        left: '0 !important',
        right: '0 !important',
        width: '100% !important',
        transform: 'none !important',
        zIndex: 1300,
        marginTop: '4px',
        ...props.sx,
      }}
    />
  );
};

// Override styles to remove minWidth and handle multi-select height
const MultiSelectAutocomplete = styled(StyledAutocomplete)(({ theme }) => ({
  minWidth: 'unset !important', 
  '& .MuiOutlinedInput-root': {
    height: 'auto !important', // Let it expand vertically for multiple chips
    minHeight: '36px',
    padding: '2px 8px !important', // Vertical padding to prevent tight layout
    '& .MuiAutocomplete-input': {
      padding: '4px !important',
    }
  },
  '& .MuiChip-root': {
    height: '24px', // Smaller chips
    fontSize: '0.75rem',
    margin: '1px',
    backgroundColor: '#f1f5f9',
    '& .MuiChip-deleteIcon': {
      fontSize: '16px',
    }
  }
}));

const PaginatedMultiSelect = <T extends OptionType>({
  fetchOptions,
  getOptionLabel,
  value,
  onChange,
  label = "Select Items...",
  initialInputValue = "",
  placeholder,
  size = 'small',
  multiple = true, // Default to true
  ...rest
}: React.PropsWithChildren<PaginatedSearchSelectProps<T>>): React.ReactElement => {

  const [open, setOpen] = useState(false);
  // Ensure options always contains the selected values
  const [options, setOptions] = useState<T[]>(Array.isArray(value) ? value : (value ? [value] : []));
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [inputValue, setInputValue] = useState<string>(initialInputValue);

  const valueRef = useRef(value);
  const lastFetchedPageRef = useRef(0);
  const isFetchingRef = useRef(false);
  const currentQueryRef = useRef(inputValue);
  const hasMoreRef = useRef(true);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // --- Data Fetching ---
  const loadData = useCallback(async (searchQuery: string, pageNumber: number, reset: boolean = false) => {
    if (reset) {
      currentQueryRef.current = searchQuery;
    }

    if (!reset && (!hasMoreRef.current || pageNumber <= lastFetchedPageRef.current)) return;
    if (isFetchingRef.current && !reset) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const response = await fetchOptions(searchQuery, pageNumber);

      if (searchQuery !== currentQueryRef.current) return;

      setOptions(prev => {
        const incomingOptions = response.options || [];
        const newOptions = reset ? incomingOptions : [...prev, ...incomingOptions];

        // Preserve selected values
        const currentValue = valueRef.current;
        if (currentValue) {
          const values = Array.isArray(currentValue) ? currentValue : [currentValue];
          const missingValues = values.filter(val => !newOptions.some(opt => opt.id === val.id));
          newOptions.unshift(...missingValues);
        }

        // Remove duplicates based on ID (robust O(N) using Map with string keys)
        const uniqueMap = new Map();
        newOptions.forEach(opt => {
          if (opt && opt.id != null) {
            const key = String(opt.id);
            if (!uniqueMap.has(key)) {
              uniqueMap.set(key, opt);
            }
          }
        });
        return Array.from(uniqueMap.values());
      });

      setHasMore(response.hasMore);
      hasMoreRef.current = response.hasMore;
      setPage(pageNumber);
      lastFetchedPageRef.current = pageNumber;
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchOptions]); 

  // --- Effects ---

  // 1. Debounced Search
  useEffect(() => {
    if (!open) return;
    
    // Prevent redundant resets if query hasn't changed
    if (inputValue === currentQueryRef.current && lastFetchedPageRef.current > 0) return;

    const timer = setTimeout(() => {
      loadData(inputValue, 1, true);
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue, loadData, open]);

  // 2. Open/Close
  useEffect(() => {
    if (open) {
      loadData(inputValue, 1, true);
    } else {
      // Reset options to selected values only when closed? 
      // Or keep them? Usually resetting page is good.
      setPage(1);
      lastFetchedPageRef.current = 0;
      setHasMore(true);
      hasMoreRef.current = true;
      setInputValue(""); // Clear search input on close for multi-select
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 3. Sync Input?? For multi-select, input is usually "search", not the label of selected item.
  // We DON'T want to set setInputValue to the selected item label like in single select.

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (loading || !hasMore) return;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 20) {
      loadData(inputValue, page + 1, false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: rest.fullWidth ? '100%' : 'auto', display: 'inline-block' }}>
      <MultiSelectAutocomplete
        {...rest}
        disablePortal
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        PopperComponent={CustomPopper}

        value={value || []} // Ensure array
        onChange={(event: React.SyntheticEvent, newValue: any) => {
          onChange(newValue);
          // Don't modify inputs here, let them type
        }}

        inputValue={inputValue}
        onInputChange={(event: React.SyntheticEvent, newInputValue: string, reason: string) => {
          if (reason === 'reset' && multiple) {
            // Prevent clearing input on select if we want to keep typing? 
            // Actually default behavior is 'reset' clears it, which is good for picking next item.
            setInputValue("");
          } else {
            setInputValue(newInputValue);
          }
        }}

        options={options}
        getOptionLabel={(option: T) => (option[getOptionLabel] as string) || ''}
        isOptionEqualToValue={(option: T, val: T) => option.id === val.id}
        filterOptions={(x: T[]) => x}

        loading={loading}
        popupIcon={<CustomDropdownIcon />}

        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldStyled
            {...params}
            placeholder={placeholder}
            size={size}
            error={rest.error}
            helperText={rest.helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: handleScroll
        }}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: T) => (
          <Box component="li" {...props} key={option.id}>
            <Typography noWrap>{option[getOptionLabel] as string}</Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default PaginatedMultiSelect;
